import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

/**
 * Hooks for custom BNBA datasets (`/api/v1/bnba-datasets`, admin only): pick
 * the approved fields of a DTSEN set, build them into a gated endpoint.
 * See docs/bnba-custom-dataset-design.md.
 */

export type DtsenSet = 'keluarga' | 'anggota';
export type DtsenSensitivity = 'none' | 'quasi-id' | 'pii';

export interface DtsenField {
  name: string;
  set: DtsenSet;
  order: number;
  group: string;
  type: string;
  definition: string;
  codes?: Record<string, string>;
  sensitivity: DtsenSensitivity;
  filterable: boolean;
}

export interface BnbaSource {
  dataSourceId: string;
  name: string;
  set: DtsenSet;
  filename: string;
  rowCount: number;
  uploadedAt: string;
  jobId: string | null;
  buildable: boolean;
  missingFields: string[];
}

export interface BnbaCatalog {
  joinKey: string;
  joinPrefix: Record<DtsenSet, string>;
  fields: DtsenField[];
  sources: BnbaSource[];
}

export type CustomDatasetStatus = 'DRAFT' | 'BUILDING' | 'READY' | 'FAILED' | 'REVOKED';

export interface CustomDataset {
  id: string;
  name: string;
  tableName: string;
  requester: string;
  approvalRef: string;
  periode: string;
  sourceSet: DtsenSet;
  fields: string[];
  joinFields: string[];
  filters: Record<string, string[]>;
  joinFilters: Record<string, string[]>;
  expiresAt: string | null;
  status: CustomDatasetStatus;
  progress: {
    phase: 'join' | 'rows' | 'done';
    batchesDone: number;
    batchesTotal: number;
    rowsRead: number;
    rowsWritten: number;
  } | null;
  error: string | null;
  generation: number;
  builtAt: string | null;
  createdAt: string;
  columns: string[];
  sensitiveColumns: Array<{ column: string; sensitivity: DtsenSensitivity }>;
  building: boolean;
  dataset: { id: string; recordCount: number; isPublic: boolean; expiresAt: string | null } | null;
  accessUrl?: string | null;
}

export interface CreateCustomDatasetInput {
  name: string;
  tableName: string;
  requester: string;
  approvalRef: string;
  periode: string;
  sourceSet: DtsenSet;
  dataSourceId: string;
  fields: string[];
  filters: Record<string, string[]>;
  joinDataSourceId?: string;
  joinFields: string[];
  joinFilters: Record<string, string[]>;
  expiresAt?: string;
  piiConfirmed: boolean;
}

export const bnbaDatasetKeys = {
  all: ['bnba-datasets'] as const,
  catalog: () => [...bnbaDatasetKeys.all, 'catalog'] as const,
  list: () => [...bnbaDatasetKeys.all, 'list'] as const,
  detail: (id: string) => [...bnbaDatasetKeys.all, 'detail', id] as const,
};

export function useBnbaCatalog() {
  return useQuery({
    queryKey: bnbaDatasetKeys.catalog(),
    queryFn: () => apiClient.get<BnbaCatalog>('/bnba-datasets/catalog'),
    staleTime: 60 * 1000,
  });
}

export function useBnbaDatasets() {
  return useQuery({
    queryKey: bnbaDatasetKeys.list(),
    queryFn: async () =>
      (await apiClient.get<{ datasets: CustomDataset[] }>('/bnba-datasets')).datasets,
    // Poll only while something is building.
    refetchInterval: (query) =>
      query.state.data?.some((d) => d.status === 'BUILDING') ? 3000 : false,
  });
}

export function useBnbaDataset(id: string | null) {
  return useQuery({
    queryKey: bnbaDatasetKeys.detail(id ?? ''),
    enabled: Boolean(id),
    queryFn: async () =>
      (await apiClient.get<{ dataset: CustomDataset }>(`/bnba-datasets/${id}`)).dataset,
  });
}

export function useCreateBnbaDataset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateCustomDatasetInput) => {
      const created = await apiClient.post<{ dataset: CustomDataset }>('/bnba-datasets', input);
      await apiClient.post(`/bnba-datasets/${created.dataset.id}/build`);
      return created.dataset;
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: bnbaDatasetKeys.all }),
  });
}

export function useBuildBnbaDataset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.post(`/bnba-datasets/${id}/build`),
    onSettled: () => queryClient.invalidateQueries({ queryKey: bnbaDatasetKeys.all }),
  });
}

export function useRevokeBnbaDataset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.post(`/bnba-datasets/${id}/revoke`),
    onSettled: () => queryClient.invalidateQueries({ queryKey: bnbaDatasetKeys.all }),
  });
}

/** Message and, for a 422, the list of reasons the request cannot be built. */
export function bnbaErrorOf(error: unknown): { message: string; problems: string[] } {
  const data = (error as any)?.response?.data;
  return {
    message: data?.message || (error as Error)?.message || 'Permintaan gagal',
    problems: Array.isArray(data?.problems) ? data.problems : [],
  };
}
